import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ProductImage, ImageProtectionType } from '../../utils/types';
import ProtectedImage from '../common/ProtectedImage';
import MagnifyingGlass from '../common/MagnifyingGlass';
import { getImageStorageSettings } from '../../services/imageStorageService';

// استخدام مسار نسبي للصورة الافتراضية
const placeholderImage = '/assets/images/product-placeholder.png';

interface ProductImageGalleryProps {
  mainImage?: string;
  additionalImages?: ProductImage[];
  productName: string;
}

const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  mainImage,
  additionalImages = [],
  productName
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentImage, setCurrentImage] = useState<string>('');
  const [lightboxOpen, setLightboxOpen] = useState<boolean>(false);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const [imageProtection, setImageProtection] = useState<ImageProtectionType>(ImageProtectionType.NONE);
  const [watermarkText, setWatermarkText] = useState<string>('');

  // تحميل إعدادات حماية الصور
  useEffect(() => {
    const loadImageProtectionSettings = async () => {
      try {
        const settings = await getImageStorageSettings();
        setImageProtection(settings.imageProtection || ImageProtectionType.NONE);
        setWatermarkText(settings.watermarkText || '');
      } catch (error) {
        console.error('Error loading image protection settings:', error);
      }
    };

    loadImageProtectionSettings();
  }, []);

  // تجميع جميع الصور في مصفوفة واحدة
  const allImages: string[] = [];

  // إضافة الصورة الرئيسية إذا كانت موجودة
  if (mainImage) {
    allImages.push(mainImage);
  }

  // إضافة الصور الإضافية
  if (additionalImages && additionalImages.length > 0) {
    // ترتيب الصور حسب الصورة الرئيسية أولاً
    const mainImages = additionalImages
      .filter(img => img.isMain)
      .map(img => img.url);

    const otherImages = additionalImages
      .filter(img => !img.isMain)
      .map(img => img.url);

    // إضافة الصور المرتبة إلى المصفوفة
    allImages.push(...mainImages.filter(url => url !== mainImage));
    allImages.push(...otherImages);
  }

  // إذا لم تكن هناك صور، استخدم الصورة الافتراضية
  if (allImages.length === 0) {
    allImages.push(placeholderImage);
  }

  // تعيين الصورة الحالية عند تحميل المكون
  useEffect(() => {
    if (allImages.length > 0) {
      setCurrentImage(allImages[0]);
    }
  }, [mainImage, additionalImages]);

  // تغيير الصورة الحالية
  const handleImageChange = (image: string) => {
    setCurrentImage(image);
  };

  // فتح معرض الصور بالحجم الكامل
  const openLightbox = (index: number) => {
    // إذا كانت هناك حماية لمنع الفتح في صفحة مستقلة، لا تفتح المعرض
    if (imageProtection === ImageProtectionType.PREVENT_OPEN ||
        imageProtection === ImageProtectionType.FULL) {
      return;
    }

    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // إغلاق معرض الصور
  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // الانتقال إلى الصورة التالية في معرض الصور
  const nextImage = () => {
    setLightboxIndex((prevIndex) =>
      prevIndex === allImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  // الانتقال إلى الصورة السابقة في معرض الصور
  const prevImage = () => {
    setLightboxIndex((prevIndex) =>
      prevIndex === 0 ? allImages.length - 1 : prevIndex - 1
    );
  };

  // تحديد ما إذا كان يمكن فتح الصور في المعرض
  const canOpenLightbox = imageProtection !== ImageProtectionType.PREVENT_OPEN &&
                          imageProtection !== ImageProtectionType.FULL;

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        {/* الصور المصغرة - تظهر على الجانب في الشاشات الكبيرة وأعلى الصورة الرئيسية في الشاشات الصغيرة */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'row', md: 'column' },
          gap: 1,
          overflow: { xs: 'auto', md: 'auto' },
          maxWidth: { xs: '100%', md: '100px' },
          maxHeight: { xs: '100px', md: '500px' },
          order: { xs: 1, md: 1 },
          py: { xs: 1, md: 0 }
        }}>
          {allImages.map((image, index) => (
            <Paper
              key={index}
              elevation={2}
              sx={{
                width: { xs: 80, md: 80 },
                height: { xs: 80, md: 80 },
                minWidth: { xs: 80, md: 80 },
                minHeight: { xs: 80, md: 80 },
                cursor: 'pointer',
                border: currentImage === image ? `2px solid ${theme.palette.primary.main}` : '1px solid #eee',
                borderRadius: 1,
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                  boxShadow: theme.shadows[4]
                },
              }}
              onClick={() => handleImageChange(image)}
            >
              <ProtectedImage
                src={image}
                alt={`${productName} - صورة ${index + 1}`}
                forceProtection={ImageProtectionType.PREVENT_SAVE}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  p: 1
                }}
              />
            </Paper>
          ))}
        </Box>

        {/* الصورة الرئيسية */}
        <Box
          sx={{
            width: '100%',
            position: 'relative',
            backgroundColor: '#f9f9f9',
            borderRadius: 2,
            overflow: 'hidden',
            order: { xs: 2, md: 2 },
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #eee',
            p: 2,
            minHeight: { xs: 300, sm: 400, md: 500 },
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
                '& img': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                }
              },
            }}
          >
            <MagnifyingGlass
              src={currentImage}
              alt={productName}
              watermarkText={watermarkText}
              imageProtection={imageProtection}
              watermarkPosition="center"
              zoomFactor={2}
              magnifierSize={180}
              onClick={canOpenLightbox ? () => openLightbox(allImages.indexOf(currentImage)) : undefined}
            />
          </Box>

          {/* زر التكبير - يظهر فقط إذا كان مسموح بفتح المعرض */}
          {canOpenLightbox && (
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
              onClick={() => openLightbox(allImages.indexOf(currentImage))}
            >
              <ZoomInIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* معرض الصور بالحجم الكامل */}
      {lightboxOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: 'white',
            }}
            onClick={closeLightbox}
          >
            <CloseIcon fontSize="large" />
          </IconButton>

          <IconButton
            sx={{
              position: 'absolute',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
            onClick={prevImage}
          >
            <NavigateBeforeIcon fontSize="large" />
          </IconButton>

          <Box sx={{ width: '80%', height: '80%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <MagnifyingGlass
              src={allImages[lightboxIndex]}
              alt={`${productName} - صورة ${lightboxIndex + 1}`}
              watermarkText={watermarkText}
              imageProtection={imageProtection}
              watermarkPosition="center"
              zoomFactor={3}
              magnifierSize={250}
            />
          </Box>

          <IconButton
            sx={{
              position: 'absolute',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              }
            }}
            onClick={nextImage}
          >
            <NavigateNextIcon fontSize="large" />
          </IconButton>

          {/* عداد الصور */}
          <Typography
            sx={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '4px 12px',
              borderRadius: 1,
            }}
          >
            {lightboxIndex + 1} / {allImages.length}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default ProductImageGallery;
